import { Link } from '@tanstack/react-router';
import { MarketingShell } from '@/components/marketing/marketing-shell';
import { MarkdownBody } from '@/components/marketing/markdown-body';
import {
  SeoHead,
  articleJsonLd,
  breadcrumbJsonLd,
  faqJsonLd,
  organizationJsonLd,
} from '@/components/marketing/seo-head';
import { formatBlogDate, getBlogPost, getAllBlogPosts, stripLeadingH1 } from '@/lib/blog';
import { BRAND, NICHE_TO_TRADE_TYPE, type NicheSlug } from '@maintenance-tracker/shared';
import { signupSearchForNiche } from '@/lib/niche-signup';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

type BlogPostPageProps = {
  slug: string;
};

/** Extract FAQ pairs from markdown ## FAQ section for JSON-LD. */
function extractFaqsFromContent(content: string): { q: string; a: string }[] {
  const faqIdx = content.search(/^## FAQ/m);
  if (faqIdx === -1) return [];
  const faqSection = content.slice(faqIdx);
  const matches = [...faqSection.matchAll(/^### (.+)\n\n([\s\S]*?)(?=\n### |\n## |$)/gm)];
  return matches.map((m) => ({ q: m[1].trim(), a: m[2].replace(/\n/g, ' ').trim() }));
}

export function BlogPostPage({ slug }: BlogPostPageProps) {
  const post = getBlogPost(slug);

  if (!post) {
    return (
      <MarketingShell>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="text-2xl font-semibold">Article not found</h1>
          <Link to="/blog" className="mt-4 inline-block text-brand hover:underline">
            ← Back to blog
          </Link>
        </div>
      </MarketingShell>
    );
  }

  const { meta, content } = post;
  const path = `/blog/${meta.slug}`;
  const faqs = extractFaqsFromContent(content);
  const ctaNiche = (
    meta.ctaNiche && meta.ctaNiche in NICHE_TO_TRADE_TYPE ? meta.ctaNiche : 'hvac'
  ) as NicheSlug;
  const signupSearch = signupSearchForNiche(ctaNiche);
  const relatedNiche = meta.relatedLandingPages?.[0]?.replace(/^\/for\//, '') as NicheSlug | undefined;

  const related = (meta.relatedPosts ?? [])
    .map((s) => getAllBlogPosts().find((p) => p.meta.slug === s))
    .filter(Boolean)
    .slice(0, 3);

  const jsonLd = [
    organizationJsonLd(),
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: meta.title, path },
    ]),
    articleJsonLd({
      title: meta.title,
      description: meta.description,
      path,
      published: meta.published,
      updated: meta.updated,
      author: meta.author,
    }),
    ...(faqs.length ? [faqJsonLd(faqs)] : []),
  ];

  return (
    <MarketingShell ctaSearch={signupSearch}>
      <SeoHead
        title={meta.title}
        description={meta.description}
        canonicalPath={path}
        ogType="article"
        jsonLd={jsonLd}
      />

      <article className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-brand">
          <ArrowLeft className="h-4 w-4" /> Blog
        </Link>

        <header className="mt-6 border-b border-border pb-8">
          <div className="flex flex-wrap items-center gap-2 text-[13px] text-text-muted">
            <span className="capitalize">{meta.pillar}</span>
            <span>·</span>
            <time dateTime={meta.published}>Published {formatBlogDate(meta.published)}</time>
            {meta.updated !== meta.published && (
              <>
                <span>·</span>
                <span>Updated {formatBlogDate(meta.updated)}</span>
              </>
            )}
          </div>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">{meta.title}</h1>
          <p className="mt-4 text-lg text-text-secondary">{meta.description}</p>
          <p className="mt-2 text-sm text-text-muted">By {meta.author}</p>
        </header>

        <MarkdownBody content={stripLeadingH1(content)} className="mt-10" />

        <div className="mt-14 rounded-2xl border border-brand-border bg-brand-subtle p-8 text-center">
          <h2 className="text-xl font-semibold">See your revenue at risk — free</h2>
          <p className="mx-auto mt-3 max-w-md text-text-secondary">
            Import your customer list and see who is likely to lapse in the next 90 days. {DEFAULT_TRIAL_LINE}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/signup" search={signupSearch}>
              <Button size="lg">Start free trial</Button>
            </Link>
            {relatedNiche && relatedNiche in NICHE_TO_TRADE_TYPE && (
              <Link to="/for/$niche" params={{ niche: relatedNiche }}>
                <Button size="lg" variant="secondary">
                  {BRAND.name} for your trade
                </Button>
              </Link>
            )}
          </div>
        </div>

        {related.length > 0 && (
          <aside className="mt-14 border-t border-border pt-10">
            <h2 className="text-lg font-semibold">Related articles</h2>
            <ul className="mt-4 space-y-3">
              {related.map((r) =>
                r ? (
                  <li key={r.meta.slug}>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: r.meta.slug }}
                      className="flex items-center gap-2 text-brand hover:underline"
                    >
                      <ArrowRight className="h-4 w-4" />
                      {r.meta.title}
                    </Link>
                  </li>
                ) : null,
              )}
            </ul>
          </aside>
        )}
      </article>
    </MarketingShell>
  );
}

const DEFAULT_TRIAL_LINE = '7-day trial, no credit card.';
